// hooks/useDeleteSuppliers.ts
import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry';

interface DeleteOptions {
  deleteContacts?: boolean;
  buyerOrgId?: string;
}

export const useDeleteSuppliers = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteSuppliers = async (
    supplierIds: string[],
    options: DeleteOptions = {},
  ) => {
    setLoading(true);
    setError(null);

    try {
      for (const id of supplierIds) {
        if (options.buyerOrgId) {
          const { data: linkRows, error: linkError } = await supabase
            .from('buyer_supplier_link')
            .select('id')
            .eq('buyer_org_id', options.buyerOrgId)
            .eq('supplier_id', id);

          if (linkError) {
            console.error('Error fetching buyer-supplier links:', linkError);
            setError(linkError.message);
            setLoading(false);
            return false;
          }

          const linkIds = (linkRows ?? [])
            .map((row) => row.id)
            .filter((linkId): linkId is string => Boolean(linkId));

          if (linkIds.length > 0) {
            const { error: assessmentError } = await supabase
              .from('assessments')
              .update({ deleted: true })
              .in('link_id', linkIds);

            if (assessmentError) {
              console.error(
                'Error soft-deleting linked assessments:',
                assessmentError,
              );
              setError(assessmentError.message);
              setLoading(false);
              return false;
            }
          }
        }

        const { error } = await supabase.rpc('delete_supplier', {
          p_supplier_id: id,
          delete_contacts: options.deleteContacts ?? false,
        });

        if (error) {
          console.error('Error deleting supplier:', error);
          setError(error.message);
          setLoading(false);
          return false;
        }
      }

      setLoading(false);
      return true;
    } catch (err) {
      captureSupabaseError(err, { op: 'supplier.delete' });
      console.error('Unexpected error deleting suppliers:', err);
      setError((err as Error).message);
      setLoading(false);
      return false;
    }
  };

  return { deleteSuppliers, loading, error };
};
